/*
 * Cart-drawer recommended-product quick-add card.
 *
 * One <cart-rec-card> per recommended product inside the cart drawer's
 * "Misschien vind je dit ook leuk" block (snippets/product-card-horizontal.liquid
 * rendered with cart_quick_add). Lets the shopper pick colour (when present) and
 * size and add the EXACT resolved variant straight to the cart — no quick-view,
 * no detour to the product page.
 *
 * Stock-aware, exactly like the product page "Vaak samen gekocht" block:
 *   - in-stock size  -> selectable, adds normally
 *   - pre-order size -> selectable, adds WITH the same 'Pre-order' line-item
 *                       property the native buy button sends
 *   - sold-out size  -> shown as sold out (dimmed + struck, not selectable).
 *                       No back-in-stock / notify-me in the cart on purpose.
 *
 * Variant/option/swatch resolution mirrors bundle-save.js so the swatches and
 * size buttons read identically to the rest of the store. The add -> cart-drawer
 * refresh flow is the proven bundle-save flow (POST /cart/add.js, re-fetch
 * /cart.js, publish cartUpdate, open the drawer) so the cart, subtotal and
 * free-shipping bar update cleanly with no flash.
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('cart-rec-card')) return;

  var ONE_SIZE = '__one__';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents) {
    try {
      var formatted = theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      var temp = document.createElement('div');
      temp.innerHTML = formatted;
      return temp.textContent || temp.innerText || formatted;
    } catch (e) {
      return '€ ' + (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    }
  }

  function imageWide(src, width) {
    if (!src) return src;
    var base = String(src).split('?')[0].trim();
    if (!base) return src;
    return base + '?width=' + width;
  }

  class CartRecCard extends HTMLElement {
    connectedCallback() {
      // Idempotent: the cart drawer re-injects the preserved recommendations HTML
      // on every cart update (cart.js onCartUpdate), which re-runs this callback
      // on freshly parsed nodes. Guard so a live, already-wired card is never
      // double-bound, and rebuild cleanly from the data island otherwise.
      if (this._init) return;
      this._init = true;

      this.ui = this.querySelector('[data-cart-rec-ui]');
      var data = this.parseIsland('[data-cart-rec-data]');
      if (!this.ui || !data) {
        this.hidden = true;
        return;
      }

      this.p = this.buildModel(data);
      if (!this.p.variants.length) {
        this.hidden = true;
        return;
      }

      var initColor = null;
      if (this.p.colorIdx >= 0) {
        var io = data.initialOptions;
        var pref = io ? io[this.p.colorIdx] : null;
        // Liquid (snippets/cart-recommendations.liquid) pre-selects the colour
        // matching this cart item; honour it when it is an offered + in-stock
        // colour, otherwise fall back to the first available offered colour.
        if (pref && this.isAllowedColor(this.p, pref) && this.colorAvailable(this.p, pref)) {
          initColor = pref;
        } else {
          initColor = this.firstAvailableColor(this.p);
        }
      }
      this.p.colorValue = initColor;
      this.p.sizeValue = null;

      this.render();
    }

    attr(name, fallback) {
      var v = this.getAttribute(name);
      return v == null || v === '' ? fallback : v;
    }

    parseIsland(selector) {
      var el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    buildModel(data) {
      var options = Array.isArray(data.options) ? data.options : [];
      var variants = Array.isArray(data.variants) ? data.variants : [];
      var allowedColors = this.parseAllowedColors();
      var colorIdx = options.findIndex(function (o) {
        return o.isColor;
      });
      var sizeIdx = options.findIndex(function (o) {
        return o.isSize;
      });
      if (sizeIdx < 0) {
        sizeIdx = options.findIndex(function (o, i) {
          return i !== colorIdx;
        });
        if (sizeIdx >= 0) {
          var vals = options[sizeIdx].values || [];
          if (vals.length === 1 && vals[0] && vals[0].name === 'Default Title') sizeIdx = -1;
        }
      }
      return {
        id: data.id,
        title: data.title,
        url: data.url,
        fallbackImage: data.fallbackImage,
        initialOptions: data.initialOptions,
        sizeChartId: data.sizeChartId,
        options: options,
        variants: variants,
        colorIdx: colorIdx,
        sizeIdx: sizeIdx,
        allowedColors: allowedColors,
        colorValue: null,
        sizeValue: null
      };
    }

    /* ----- allowed colours (custom.cart_recommendations curation) ----- */

    // The metafield curates which colours a recommendation offers. Parsed from
    // the pipe-delimited data-allowed-colors attribute; null = show every colour.
    parseAllowedColors() {
      var raw = this.getAttribute('data-allowed-colors');
      if (raw == null || raw === '') return null;
      var list = raw
        .split('|')
        .map(function (s) {
          return s.trim().toLowerCase();
        })
        .filter(Boolean);
      return list.length ? list : null;
    }

    isAllowedColor(p, color) {
      if (!p.allowedColors) return true;
      return p.allowedColors.indexOf(String(color).toLowerCase()) !== -1;
    }

    // The colour option values actually offered for this card. When the metafield
    // curates a colour set we show only those; if the filter would empty the list
    // we fall back to every colour so the card is never left without swatches.
    allowedColorValues(p) {
      if (p.colorIdx < 0) return [];
      var opt = p.options[p.colorIdx];
      if (!opt || !Array.isArray(opt.values)) return [];
      if (!p.allowedColors) return opt.values;
      var allowed = p.allowedColors;
      var filtered = opt.values.filter(function (val) {
        return allowed.indexOf(String(val.name).toLowerCase()) !== -1;
      });
      return filtered.length ? filtered : opt.values;
    }

    /* ----- availability ----- */

    colorAvailable(p, color) {
      // Round 24: a pre-order variant IS purchasable here (oversold with
      // continue-selling), so it counts as available — a pre-order-only colour
      // stays selectable, mirroring the "Vaak samen gekocht" pack builder. Only
      // a genuine sold-out colour is dimmed/skipped by firstAvailableColor.
      return p.variants.some(function (v) {
        return v.available && (p.colorIdx < 0 || v.options[p.colorIdx] === color);
      });
    }

    firstAvailableColor(p) {
      if (p.colorIdx < 0) return null;
      var values = this.allowedColorValues(p);
      if (!values.length) return null;
      for (var i = 0; i < values.length; i++) {
        if (this.colorAvailable(p, values[i].name)) return values[i].name;
      }
      return values[0] ? values[0].name : null;
    }

    sizeAvailableForColor(p, sizeVal) {
      if (p.sizeIdx < 0) return true;
      // Round 24: pre-order counts as available here (it is purchasable), so a
      // pre-order size stays selectable and gets the "Pre-order" badge. Only a
      // genuine sold-out / non-existent combo is struck and not selectable.
      return p.variants.some(function (v) {
        return (
          v.available &&
          (p.colorIdx < 0 || v.options[p.colorIdx] === p.colorValue) &&
          v.options[p.sizeIdx] === sizeVal
        );
      });
    }

    // The exact variant for the current colour + a given size value, whether or
    // not it is available (needed to read its real stock state: in-stock vs
    // pre-order vs sold-out vs non-existent combo).
    valueVariant(sizeVal) {
      var p = this.p;
      var cands = p.variants.filter(function (v) {
        return (
          (p.colorIdx < 0 || v.options[p.colorIdx] === p.colorValue) &&
          (p.sizeIdx < 0 || sizeVal === ONE_SIZE || v.options[p.sizeIdx] === sizeVal)
        );
      });
      return cands.filter(function (v) {
          return v.available;
        })[0] || cands[0] || null;
    }

    resolveVariant(p) {
      var cands = p.variants.filter(function (v) {
        return (
          (p.colorIdx < 0 || v.options[p.colorIdx] === p.colorValue) &&
          (p.sizeIdx < 0 || p.sizeValue == null || p.sizeValue === ONE_SIZE || v.options[p.sizeIdx] === p.sizeValue)
        );
      });
      // Round 24: a pre-order variant is purchasable, so prefer any available
      // variant (in-stock or pre-order) for this colour+size. The exact resolved
      // variant's own .preorder flag drives the badge + line-item property.
      var avail = cands.filter(function (v) {
        return v.available;
      });
      return avail[0] || cands[0] || null;
    }

    displayVariant(p) {
      var v = this.resolveVariant(p);
      return v || p.variants[0] || null;
    }

    /* ----- render ----- */

    render() {
      var p = this.p;
      var colorOpt = p.colorIdx >= 0 ? p.options[p.colorIdx] : null;

      var colorLabel = '';
      var swatchHtml = '';
      if (colorOpt) {
        colorLabel =
          '<div class="cart-rec-card__opt-label cart-rec-card__opt-label--color">' +
          escapeHtml(colorOpt.name) +
          ': <span data-cart-rec-color-name></span></div>';
        swatchHtml = '<div class="cart-rec-card__swatches" data-cart-rec-swatches></div>';
      }

      var sizesHead = '';
      if (p.sizeIdx >= 0) {
        sizesHead =
          '<div class="cart-rec-card__sizes-head"><span class="cart-rec-card__opt-label">' +
          escapeHtml(p.options[p.sizeIdx].name) +
          '</span></div>';
      }

      this.ui.innerHTML =
        '<a class="cart-rec-card__media" href="' + escapeHtml(p.url || '#') + '" tabindex="-1">' +
        '<img data-cart-rec-img alt="" loading="lazy">' +
        '</a>' +
        '<div class="cart-rec-card__body">' +
        '<div class="cart-rec-card__head">' +
        '<a class="cart-rec-card__title" href="' + escapeHtml(p.url || '#') + '">' +
        escapeHtml(p.title || '') +
        '</a>' +
        '<span class="cart-rec-card__price" data-cart-rec-price></span>' +
        '</div>' +
        colorLabel +
        swatchHtml +
        sizesHead +
        '<div class="cart-rec-card__sizes" data-cart-rec-sizes></div>' +
        '<button type="button" class="button button--primary button--small cart-rec-card__add" is="hover-button" data-cart-rec-add disabled>' +
        '<span class="btn-fill" data-fill></span>' +
        '<span class="btn-text" data-cart-rec-add-text></span>' +
        '</button>' +
        '<div class="cart-rec-card__error product-form__error-message" data-cart-rec-error hidden></div>' +
        '</div>';

      this.imgEl = this.ui.querySelector('[data-cart-rec-img]');
      this.priceEl = this.ui.querySelector('[data-cart-rec-price]');
      this.colorNameEl = this.ui.querySelector('[data-cart-rec-color-name]');
      this.swatchEl = this.ui.querySelector('[data-cart-rec-swatches]');
      this.sizesEl = this.ui.querySelector('[data-cart-rec-sizes]');
      this.addBtn = this.ui.querySelector('[data-cart-rec-add]');
      this.addText = this.ui.querySelector('[data-cart-rec-add-text]');
      this.errorEl = this.ui.querySelector('[data-cart-rec-error]');

      this.renderSwatches();
      this.updateColorName();
      this.updateSizes();
      this.updateImage();
      this.updatePrice();
      this.updateAdd();

      if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd.bind(this));

      this.resizeCarousel();
    }

    renderSwatches() {
      var self = this;
      var p = this.p;
      if (!this.swatchEl || p.colorIdx < 0) return;
      var values = this.allowedColorValues(p);
      this.swatchEl.innerHTML = values
        .map(function (val) {
          var avail = self.colorAvailable(p, val.name);
          var hasSwatch = val.swatchImage || val.swatchColor;
          if (hasSwatch) {
            var style = val.swatchImage
              ? 'background-image:url(' + escapeHtml(val.swatchImage) + ')'
              : 'background-color:' + escapeHtml(val.swatchColor);
            return (
              '<button type="button" class="cart-rec-card__swatch' +
              (avail ? '' : ' is-soldout') +
              '" style="' + style + '" data-color="' + escapeHtml(val.name) +
              '" title="' + escapeHtml(val.name) +
              '" aria-pressed="false"><span class="sr-only">' + escapeHtml(val.name) + '</span></button>'
            );
          }
          return (
            '<button type="button" class="cart-rec-card__pill' +
            (avail ? '' : ' is-soldout') +
            '" data-color="' + escapeHtml(val.name) + '" aria-pressed="false">' +
            escapeHtml(val.name) + '</button>'
          );
        })
        .join('');
      this.swatchEl.querySelectorAll('[data-color]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var val = btn.getAttribute('data-color');
          if (!self.colorAvailable(p, val)) return;
          if (p.colorValue === val) return;
          p.colorValue = val;
          if (p.sizeIdx >= 0 && p.sizeValue && !self.sizeAvailableForColor(p, p.sizeValue)) {
            p.sizeValue = null;
          }
          self.updateSwatches();
          self.updateColorName();
          self.updateSizes();
          self.updateImage();
          self.updatePrice();
          self.updateAdd();
          self.resizeCarousel();
        });
      });
      this.updateSwatches();
    }

    updateSwatches() {
      var p = this.p;
      if (!this.swatchEl) return;
      this.swatchEl.querySelectorAll('[data-color]').forEach(function (btn) {
        var active = btn.getAttribute('data-color') === p.colorValue;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    updateColorName() {
      if (this.colorNameEl) this.colorNameEl.textContent = this.p.colorValue || '';
    }

    preorderBadgeHtml() {
      var label = this.attr('data-preorder-label', 'Pre-order');
      return '<span class="preorder-badge" aria-hidden="true">' + escapeHtml(label) + '</span>';
    }

    pillHtml(label, sizeVal) {
      var variant = this.valueVariant(sizeVal);
      var isPreorder = !!(variant && variant.preorder);
      // Round 24: a pre-order size is purchasable here, so it stays selectable and
      // carries the same "Pre-order" badge as the product page picker. Only a real
      // sold-out variant or a non-existent colour+size combo is struck + disabled.
      var isSoldout = !variant || !variant.available;
      var active = this.p.sizeValue === sizeVal;

      var cls = 'cart-rec-card__pill';
      if (active) cls += ' is-active';
      if (isPreorder) cls += ' is-preorder';
      if (isSoldout) cls += ' is-soldout';

      var attrs =
        ' data-size="' + escapeHtml(sizeVal) +
        '" aria-pressed="' + (active ? 'true' : 'false') + '"';
      if (isSoldout) attrs += ' aria-disabled="true"';

      return '<button type="button" class="' + cls + '"' + attrs + '>' +
        escapeHtml(label) +
        (isPreorder ? this.preorderBadgeHtml() : '') +
        '</button>';
    }

    updateSizes() {
      var self = this;
      var p = this.p;
      if (!this.sizesEl) return;

      if (p.sizeIdx < 0) {
        this.sizesEl.innerHTML = this.pillHtml(this.attr('data-one-size-label', 'Eén maat'), ONE_SIZE);
      } else {
        var opt = p.options[p.sizeIdx];
        this.sizesEl.innerHTML = opt.values
          .map(function (val) {
            return self.pillHtml(val.name, val.name);
          })
          .join('');
      }

      this.sizesEl.querySelectorAll('[data-size]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var val = btn.getAttribute('data-size');
          var variant = self.valueVariant(val);
          // Round 24: any purchasable variant (in-stock OR pre-order) is
          // selectable. Only a real sold-out / non-existent combo does nothing.
          if (!variant || !variant.available) return;
          p.sizeValue = p.sizeValue === val ? null : val;
          self.updateSizes();
          self.updateImage();
          self.updatePrice();
          self.updateAdd();
          self.resizeCarousel();
        });
      });
    }

    updateImage() {
      if (!this.imgEl) return;
      var v = this.displayVariant(this.p);
      var src = (v && v.image) || this.p.fallbackImage;
      if (!src) return;
      var next = imageWide(src, 600);
      if (this.imgEl.getAttribute('src') !== next) this.imgEl.setAttribute('src', next);
      this.imgEl.setAttribute('alt', this.p.title || '');
    }

    updatePrice() {
      if (!this.priceEl) return;
      var v = this.displayVariant(this.p);
      var price = v ? v.price : 0;
      // Real Shopify "on sale" rule: a compare-at only counts when strictly
      // higher than the price actually paid.
      var compare = v && v.compareAtPrice && v.compareAtPrice > price ? v.compareAtPrice : 0;
      // Derde prijs (creatorcode-korting): cc-creator-code.js leest deze attributen en zet er
      // de prijs met kortingscode en de badges bij, net als op de productkaarten.
      if (v) {
        this.priceEl.setAttribute('data-cc-variant', v.id);
        this.priceEl.setAttribute('data-cc-price', price);
        this.priceEl.setAttribute('data-cc-compare', compare);
        this.priceEl.removeAttribute('data-cc-sig');
        this.priceEl.classList.remove('cc-active', 'cc-has-badge');
      }
      if (compare > price) {
        this.priceEl.innerHTML =
          '<s class="cart-rec-card__price-was">' + escapeHtml(money(compare)) + '</s>' +
          '<span class="cart-rec-card__price-now cart-rec-card__price-now--sale">' + escapeHtml(money(price)) + '</span>';
      } else {
        this.priceEl.innerHTML =
          '<span class="cart-rec-card__price-now">' + escapeHtml(money(price)) + '</span>';
      }
    }

    updateAdd() {
      if (!this.addBtn) return;
      var p = this.p;
      var v = p.sizeValue ? this.resolveVariant(p) : null;
      // Round 24: the button enables for any purchasable variant — in-stock or
      // pre-order (pre-order is oversold with continue-selling).
      var ready = !!(v && v.available);
      this.addBtn.disabled = !ready;

      var label;
      if (!ready) {
        label = this.attr('data-select-size-label', 'Kies een maat');
      } else {
        label = this.attr('data-add-label', 'In winkelwagen') + ' · ' + money(v.price);
      }
      if (this.addText) this.addText.textContent = label;
      else this.addBtn.textContent = label;
    }

    handleError(message) {
      if (!this.errorEl) return;
      this.errorEl.toggleAttribute('hidden', !message);
      this.errorEl.textContent = message || '';
    }

    /* ----- add to cart ----- */

    onAdd(event) {
      if (event) event.preventDefault();
      var p = this.p;
      var v = p.sizeValue ? this.resolveVariant(p) : null;
      // Round 24: add any purchasable variant. A pre-order variant is oversold
      // (continue-selling) so it adds normally; only a real sold-out / missing
      // combo is rejected.
      if (!v || !v.available) return;
      if (this.addBtn.hasAttribute('aria-disabled')) return;

      var self = this;
      var properties = {};
      // Pre-order line item: send the same customer-facing property the native
      // buy button + pack builder send, so the order shows the expected delivery.
      if (v.preorder) {
        properties['Pre-order'] = v.preorderDate
          ? 'Wordt rond ' + v.preorderDate + ' uitgeleverd'
          : 'Wordt zo snel mogelijk uitgeleverd';
      }

      var item = { id: v.id, quantity: 1 };
      if (Object.keys(properties).length) item.properties = properties;

      var redirectToCart =
        document.body.classList.contains('template-cart') ||
        (theme.settings && theme.settings.cartType === 'page');

      this.handleError();
      this.activeElement = this.addBtn;

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      var body = JSON.stringify({
        items: [item],
        sections: sectionsToBundle,
        sections_url: window.location.pathname
      });

      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.setAttribute('aria-busy', 'true');

      fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body: body })
        .then(function (response) {
          return response.json();
        })
        .then(async function (parsedState) {
          if (parsedState.status) {
            self.handleError(parsedState.description);
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:error', {
                detail: { errorMessage: parsedState.description }
              })
            );

            var failCart = await (
              await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
            ).json();

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-rec-card',
              cart: failCart
            });
            return;
          }

          var cartJson = await (
            await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
          ).json();

          cartJson.sections = parsedState.sections;

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-rec-card',
            cart: cartJson
          });

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } })
          );

          if (redirectToCart) {
            window.location.href = theme.routes.cart_url;
            return;
          }

          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show(self.activeElement);
          }

          // Reset the selection so the card is ready for the next pick. The cart
          // drawer preserves/re-injects this block, so this live reset keeps the
          // visible state consistent until the re-inject re-runs render().
          p.sizeValue = null;
          self.updateSizes();
          self.updateImage();
          self.updatePrice();
          self.updateAdd();
          self.resizeCarousel();
        })
        .catch(function () {
          self.handleError(self.attr('data-error-label', 'Er ging iets mis. Probeer het opnieuw.'));
        })
        .finally(function () {
          self.addBtn.removeAttribute('aria-busy');
          self.addBtn.removeAttribute('aria-disabled');
        });
    }

    /* ----- carousel ----- */

    // The cart recommendations sit in a flickity carousel whose height is set by
    // theme.Carousel (adaptiveHeight) at load() time — which runs BEFORE this
    // card builds its UI. Nudge the carousel to remeasure once the card content
    // (and any colour/size re-render) is laid out, so the cell never clips.
    resizeCarousel() {
      var cc = this.closest('product-complementary');
      if (!cc) return;
      requestAnimationFrame(function () {
        try {
          if (cc.carousel && cc.carousel.slider && typeof cc.carousel.slider.resize === 'function') {
            cc.carousel.slider.resize();
          }
        } catch (e) {}
      });
    }
  }

  customElements.define('cart-rec-card', CartRecCard);
})();
